(() => {
  function encodeWav(chunks, rate) {
    if (!Number.isInteger(rate) || rate < 8000 || rate > 48000) throw new Error('Unsupported sample rate');
    const frames = Math.min(chunks.reduce((count, chunk) => count + chunk.length, 0), rate * 30);
    const bytes = new ArrayBuffer(44 + frames * 2), view = new DataView(bytes);
    const text = (at, value) => [...value].forEach((letter, index) => view.setUint8(at + index, letter.charCodeAt(0)));
    text(0, 'RIFF'); view.setUint32(4, 36 + frames * 2, true); text(8, 'WAVE'); text(12, 'fmt ');
    view.setUint32(16, 16, true); view.setUint16(20, 1, true); view.setUint16(22, 1, true);
    view.setUint32(24, rate, true); view.setUint32(28, rate * 2, true); view.setUint16(32, 2, true);
    view.setUint16(34, 16, true); text(36, 'data'); view.setUint32(40, frames * 2, true);
    let at = 44;
    for (const chunk of chunks) for (const value of chunk) {
      if (at >= bytes.byteLength) break;
      const sample = Number.isFinite(value) ? Math.max(-1, Math.min(1, value)) : 0;
      view.setInt16(at, sample < 0 ? sample * 32768 : sample * 32767, true); at += 2;
    }
    return new Blob([bytes], {type: 'audio/wav'});
  }
  const SPEECH_RATE = 16000;
  // Box-average down to the rate speech recognition needs (also keeps the upload small).
  function downsample(chunks, from, to = SPEECH_RATE) {
    const total = chunks.reduce((count, chunk) => count + chunk.length, 0), all = new Float32Array(total);
    let at = 0; for (const chunk of chunks) { all.set(chunk, at); at += chunk.length; }
    if (from <= to) return [all];
    const ratio = from / to, out = new Float32Array(Math.floor(total / ratio));
    for (let i = 0; i < out.length; i++) {
      const start = Math.floor(i * ratio), end = Math.min(total, Math.max(start + 1, Math.floor((i + 1) * ratio)));
      let sum = 0; for (let j = start; j < end; j++) sum += all[j];
      out[i] = sum / (end - start);
    }
    return [out];
  }
  function attach({ask, input, status, voice, submit}) {
    const Context = window.AudioContext || window.webkitAudioContext;
    if (!ask.dataset.voiceEndpoint || !ask.dataset.voiceWorklet || !navigator.mediaDevices?.getUserMedia || !Context || !window.AudioWorkletNode) return null;
    const note = ask.querySelector('[data-qg-voice-note]');
    note.textContent = 'Voice sends up to 30 seconds of audio to OpenAI for transcription. Qoolala does not save the recording.';
    note.hidden = false; voice.hidden = false; ask.dataset.voiceMode = 'server';
    let active = null;
    function controls(state) {
      const pressed = state !== 'idle';
      voice.setAttribute('aria-pressed', String(pressed));
      voice.dataset.state = state === 'recording' ? 'recording' : pressed ? 'busy' : 'idle';
      voice.setAttribute('aria-label', state === 'recording' ? 'Stop recording' : pressed ? 'Cancel voice input' : 'Speak your question');
      submit.disabled = pressed;
    }
    function dispose(session) {
      clearTimeout(session.timer); clearTimeout(session.uploadTimer);
      session.node?.disconnect(); session.source?.disconnect(); session.sink?.disconnect();
      session.stream?.getTracks().forEach(track => track.stop());
      if (session.context && session.context.state !== 'closed') session.context.close().catch(() => {});
      session.chunks = [];
    }
    function cancel(announce = true) {
      const session = active; if (!session) return;
      active = null; session.controller?.abort(); dispose(session); controls('idle');
      if (announce) status.textContent = 'Voice input cancelled. You can type your question.';
    }
    async function finish(session) {
      if (active !== session || !['recording', 'finishing'].includes(session.state)) return;
      session.state = 'transcribing'; controls('transcribing'); status.textContent = 'Turning your recording into text…';
      // Measured before dispose() empties the chunks. A clip that is too short or silent never leaves the device,
      // and each failure says what is wrong rather than one message for everything.
      const rate = session.context.sampleRate, track = session.stream?.getAudioTracks()[0], device = track?.label || '';
      const peak = session.chunks.reduce((top, chunk) => chunk.reduce((m, v) => Math.max(m, Math.abs(v)), top), 0);
      const frames = session.chunks.reduce((count, chunk) => count + chunk.length, 0);
      const fail = text => { dispose(session); active = null; controls('idle'); status.textContent = text; };
      if (frames < rate * 0.5) { fail('That was too short. Press Speak, ask your question, then press Stop.'); return; }
      if (peak < 0.002) {
        fail(`We could not hear any sound${device ? ` from "${device}"` : ''}${track?.muted ? ', which is muted' : ''}. Check it is the microphone you mean to use, that it is not muted and that its input volume is up, then try again.`);
        return;
      }
      let recording;
      try { recording = encodeWav(downsample(session.chunks, rate), SPEECH_RATE); }
      catch { cancel(false); status.textContent = 'This recording could not be processed. Please type your question.'; return; }
      dispose(session);
      const controller = session.controller = new AbortController();
      session.uploadTimer = setTimeout(() => controller.abort(), 30000);
      try {
        const response = await fetch(ask.dataset.voiceEndpoint, {method: 'POST', headers: {'Content-Type': 'audio/wav'}, body: recording, signal: controller.signal});
        if (!response.ok) throw Object.assign(new Error('Transcription unavailable'), {status: response.status});
        const reply = await response.json();
        if (typeof reply.text !== 'string' || !reply.text.trim() || reply.text.length > 1000) throw new Error('Invalid transcript');
        if (active !== session) return;
        input.value = reply.text.trim(); status.textContent = 'Check your question, then press Ask.'; input.focus();
      } catch (error) {
        if (active === session) status.textContent = error.status === 422 ? 'We could not make out that recording. Speak a little closer to the microphone and try again.'
          : error.status === 429 ? 'Too many voice requests just now. Please wait a minute, or type your question.'
          : error.status ? 'Voice input is unavailable right now. Please type your question.'
          : error.name === 'AbortError' ? 'The voice service took too long. Please try again or type your question.'
          : 'We could not reach the voice service. Check your connection, or type your question.';
      } finally {
        clearTimeout(session.uploadTimer);
        if (active === session) { active = null; controls('idle'); }
      }
    }
    async function start() {
      const session = {state: 'starting', chunks: []}; active = session; controls('starting');
      status.textContent = 'Allow microphone access to speak, or choose Cancel.';
      try {
        // The microphone's own rate: forcing one on the context makes some browsers deliver silence. Downsampled on finish.
        session.context = new Context(); await session.context.resume();
        session.stream = await navigator.mediaDevices.getUserMedia({audio: {channelCount: 1, echoCancellation: true, noiseSuppression: true, autoGainControl: true}, video: false});
        if (active !== session) { dispose(session); return; }
        await session.context.audioWorklet.addModule(ask.dataset.voiceWorklet, {credentials: 'omit'});
        if (active !== session) { dispose(session); return; }
        session.node = new AudioWorkletNode(session.context, 'qoolala-capture');
        session.source = session.context.createMediaStreamSource(session.stream);
        session.sink = session.context.createGain(); session.sink.gain.value = 0;
        session.source.connect(session.node); session.node.connect(session.sink); session.sink.connect(session.context.destination);
        session.node.port.onmessage = event => {
          if (active !== session) return;
          if (event.data.samples) session.chunks.push(event.data.samples);
          if (event.data.done) finish(session);
        };
        session.stream.getTracks().forEach(track => track.addEventListener('ended', () => { if (active === session && session.state === 'recording') cancel(); }));
        session.state = 'recording'; controls('recording');
        status.textContent = 'Listening. Choose Stop when finished, or press Escape to cancel. Maximum 30 seconds.';
        session.timer = setTimeout(() => {
          if (active === session && session.state === 'recording') {
            session.state = 'finishing'; controls('finishing'); status.textContent = 'Finishing your recording…'; session.node.port.postMessage({stop: true});
          }
        }, 30000);
      } catch {
        if (active === session) {
          cancel(false); status.textContent = 'The microphone could not start. Check its permission or type your question.';
        } else dispose(session);
      }
    }
    voice.onclick = () => {
      if (!active) { start(); return; }
      if (active.state === 'recording') {
        active.state = 'finishing'; controls('finishing'); status.textContent = 'Finishing your recording…';
        active.node.port.postMessage({stop: true});
      } else cancel();
    };
    ask.addEventListener('keydown', event => { if (event.key === 'Escape') cancel(); });
    input.addEventListener('input', () => cancel(false));
    document.addEventListener('visibilitychange', () => { if (document.hidden) cancel(false); });
    window.addEventListener('pagehide', () => cancel(false));
    return {cancel};
  }
  window.QoolalaVoice = {attach, encodeWav};
})();
